import { useEffect } from 'react';
import { subscribeEvents } from './events';
import type { LiveEvent } from './events';
import { invalidate, revalidateMounted } from './queryCache';

/**
 * What a live event makes out of date. Cached keys name their project,
 * environment or server (see useResource's `key`), and events say which they
 * are about, so only the affected keys refresh — quietly, while their old
 * answer stays on screen. An event with no id refreshes just the portfolio
 * summaries, which every deployment or server change touches.
 */
const PORTFOLIO_KEYS = new Set(['org-health', 'overview']);

export function keyMatcherFor(event: LiveEvent): ((key: string) => boolean) | null {
  const type = event.type;
  const ids = [event.projectId, event.environmentId, event.serverId, event.deploymentId].filter((id): id is string => typeof id === 'string' && id !== '');
  const servers = type.startsWith('server.') || type.startsWith('readiness.');
  const deployments = /^(execution|deployment|deployment_group|component|git|magento|database)[._]/.test(type) || type.startsWith('deployment');
  if (!servers && !deployments) return null;
  return (key) => {
    if (PORTFOLIO_KEYS.has(key)) return true;
    // The live server inspection is slow and costs the server something: a
    // deployment's progress events do not re-run it. Server and readiness
    // events (an audit finished, an agent connected) do.
    if (!servers && key.endsWith(':readiness-health')) return false;
    if (servers && (key === 'servers' || key === 'servers:rows' || key.startsWith('server:'))) {
      return ids.length === 0 || key === 'servers' || key === 'servers:rows' || ids.some((id) => key.includes(id));
    }
    return ids.some((id) => key.includes(id));
  };
}

/**
 * What monitors decide: the site, component and incident state. The server
 * checks every minute and publishes no event when a check recovers, so a page
 * that waited for one would show "Degraded" until it was reloaded. The live
 * server inspection (`…:readiness-health`) is not here: it is slow and costs
 * the server something.
 */
const MONITORED = /^project:.+:(env:.+:(overview|components|health)|monitoring|incidents)$/;

export function isMonitoredKey(key: string): boolean {
  return PORTFOLIO_KEYS.has(key) || MONITORED.test(key);
}

/** How often what monitors decide is read again while the page is in view. */
export const MONITORED_REFRESH_MS = 30_000;

/** A deployment appeared or changed under an open page (the toast feed saw it). */
export function invalidateDeployment(projectId?: string, environmentId?: string): void {
  const ids = [projectId, environmentId].filter((id): id is string => Boolean(id));
  invalidate((key) => PORTFOLIO_KEYS.has(key) || ids.some((id) => key.includes(id)));
}

/** Mounted once by the dashboard: live events refresh what they touched. */
export function useLiveRefresh(): void {
  useEffect(() => {
    const unsubscribe = subscribeEvents((event) => {
      // Events may have been missed while the connection was down.
      if (event.type === 'events.reconnected') {
        revalidateMounted();
        return;
      }
      const matches = keyMatcherFor(event);
      if (matches) invalidate(matches);
    });
    // Checks that recover announce nothing: read their state again while the
    // page is in view and when it comes back into view.
    const refreshMonitored = () => {
      if (document.visibilityState === 'visible') invalidate(isMonitoredKey);
    };
    const timer = window.setInterval(refreshMonitored, MONITORED_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshMonitored);
    return () => {
      unsubscribe();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshMonitored);
    };
  }, []);
}
